import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEsAdmin } from "@/hooks/useEsAdmin";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { FileText, Upload, Eye, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  DetailLayout,
  DetailLoading,
  DetailError,
  Section,
  Field,
  StatusBadge,
  formatDate,
} from "@/components/DetailLayout";
import { LaboratorioAsignadoSection } from "@/components/LaboratorioAsignadoSection";
import { MuestrasSection } from "@/components/MuestrasSection";
import { ReportesPruebaSection } from "@/components/ReportesPruebaSection";
import { FlujoStepper } from "@/components/FlujoStepper";
import { AsignacionPanel } from "@/components/AsignacionPanel";
import { UploadDocumentoModal } from "@/components/UploadDocumentoModal";
import { DocumentoPreviewModal } from "@/components/DocumentoPreviewModal";
import { PaisesBadges } from "@/components/PaisBadge";
import { DocumentosSeleccionables } from "@/components/DocumentosSeleccionables";
import { EmisionDocumentosPanel } from "@/components/EmisionDocumentosPanel";
import { PdfSolicitudAcciones } from "@/components/PdfSolicitudAcciones";
import { useRolesEfectivos, useTienePermiso, PERMISO_SUBIR_CERTIFICADO_FIRMADO } from "@/lib/sesion";

interface PaisRef { id: string; codigo: string; nombre: string; }
/** País de un snapshot de texto: la clave llega en null si el catálogo ya no lo tiene. */
interface PaisSnapshotRef { codigo: string | null; nombre: string; }
interface SignatarioRef { id: string; nombreCompleto: string; }
interface NormaDetalle {
  id: string;
  normaId: string;
  normaCodigo: string;
  normaTitulo: string;
  laboratorioId: string | null;
  laboratorioRazonSocial: string | null;
  numReporte: string | null;
  modelosEvaluados: string | null;
  fechaEmision: string | null;
  diasVigencia: number | null;
  numImeis: number | null;
  signatarios: SignatarioRef[];
}
interface BodegaDetalle {
  mismoDomicilioFiscal: boolean;
  calle: string;
  numeroExterior: string;
  numeroInterior: string | null;
  colonia: string;
  municipio: string;
  entidadFederativa: string;
  codigoPostal: string;
  pais: string;
  nombreResponsable: string;
  telefonoResponsable: string;
}
interface TransicionLog {
  ts: string;
  transicionCodigo: string | null;
  transicionNombre: string | null;
  estadoOrigenCodigo: string | null;
  estadoDestinoCodigo: string;
  actorId: string | null;
  actorCorreo: string | null;
  motivo: string | null;
}

interface SolicitudDetalle {
  id: string;
  folio: string;
  estatus: string;
  fechaCreacion: string;

  titularId: string;
  titularRazonSocial: string;
  titularRfc: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  tramitadorRfc: string | null;

  servicioCodigo: string | null;
  servicioNombre: string | null;
  otroServicio: string | null;
  esquemaCodigo: string | null;
  esquemaNombre: string | null;
  esquemaDescripcion: string | null;
  tipoProductoCodigo: string | null;
  tipoProductoNombre: string | null;

  productoNombre: string;
  productoModelo: string;
  marca: string;
  productoAccesorios: string | null;
  productoFabricante: string | null;
  productoFraccionArancelaria: string | null;
  productoModelos: string | null;
  productoCantidadLote: number | null;
  productoCantidadSerie: number | null;
  productoNumImeis: number | null;
  paisesOrigen: PaisRef[];
  paisesProcedencia: PaisRef[];

  certificadoAnterior: string | null;
  bajoProtesta: boolean;
  numFamiliaGrupos: string | null;
  /** Número de Referencia (UPT{AA}-{NNNN}) capturado por Finanzas en PAGO_VALIDADO. */
  numReferencia: string | null;

  normas: NormaDetalle[];
  bodega: BodegaDetalle | null;
  comentario: string | null;

  instanciaId: string | null;
  estadoCodigo: string | null;
  estadoNombre: string | null;
  cerrada: boolean;
  /** Documento final del servicio (config del catálogo); null = certificado. */
  documentoResultado: string | null;
  /** Tipos de documento de la emisión configurados para el servicio. */
  tiposDocumentoFinal: string[];
  /** Certificado emitido desde esta solicitud (null si aún no se emite). */
  certificadoId: string | null;
  numCertificado: string | null;
  /** Número de pre-solicitud NT-AAMMDD-NN, asignado por SICEN al registrar (TEL-14). */
  preSolicitud: string;
  /** Ya aprobada en la etapa Solicitud: su PDF se puede consultar (TEL-14). */
  pdfSolicitudDisponible: boolean;
  historico: TransicionLog[];
}

/** Estados del flujo Telecom v1 (legacy) donde aplica el detalle técnico de Fase 2.
    En el flujo simple v2 no existen estos códigos, por eso ocultamos las
    secciones de Lab/Muestras/Reportes ahí. */
const ESTADOS_FLUJO_LEGACY = new Set([
  "ALCANCE_TECNICO_DETERMINADO",
  "MUESTRA_REQUERIDA",
  "REPORTE_REQUERIDO",
  "EVALUACION_TECNICA",
  "ACLARACION_TECNICA",
  "DECISION_CERTIFICACION",
]);
function esFlujoTecnicoLegacy(estadoCodigo: string | null): boolean {
  return !!estadoCodigo && ESTADOS_FLUJO_LEGACY.has(estadoCodigo);
}

function toneFlujo(cerrada: boolean, estadoCodigo: string | null): "ok" | "info" | "warn" | "neutral" {
  if (!estadoCodigo) return "neutral";
  if (cerrada) {
    if (estadoCodigo === "CERTIFICADO_EMITIDO") return "ok";
    return "warn";
  }
  return "info";
}

export function SolicitudTelecomDetailPage() {
  const { id } = useParams<{ id: string }>();
  const esAdmin = useEsAdmin();
  const q = useQuery({
    queryKey: ["solicitud-telecom-detalle", id],
    queryFn: async () =>
      (await api.get<SolicitudDetalle>(`/api/sector/telecom/solicitudes/${id}/detalle-completo`)).data,
    enabled: !!id,
  });

  if (q.isLoading) return <DetailLoading />;
  if (q.error || !q.data) return <DetailError />;
  const s = q.data;
  const isCU = s.servicioCodigo === "CERTIFICADO_UNICO";

  return (
    <DetailLayout
      title={s.folio}
      subtitle={
        <>
          {s.productoNombre} {s.productoModelo} · marca <span className="font-medium">{s.marca}</span>
        </>
      }
      backTo="/telecom"
      backLabel="Volver a Telecom"
      badges={
        <>
          <StatusBadge tone={toneFlujo(s.cerrada, s.estadoCodigo)}>
            {s.estadoNombre ?? s.estadoCodigo ?? "sin flujo"}
          </StatusBadge>
          {s.cerrada && <StatusBadge tone="neutral">cerrada</StatusBadge>}
        </>
      }
      rawData={s}
    >
      {/* Stepper visual del flujo (Ítem 8 doc 202605200056) */}
      <FlujoStepper estadoCodigo={s.estadoCodigo} cerrada={s.cerrada} etapaFinalNombre={s.documentoResultado} />

      {/* Enlace directo al certificado emitido (incidencia 0049: el borrador y
          el PDF firmado se gestionan en la pantalla del certificado). */}
      {s.certificadoId && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-estado-ok/25 bg-estado-ok-bg p-3">
          <div className="text-sm text-estado-ok">
            <span className="font-semibold">Certificado emitido:</span>{" "}
            <span className="font-mono">{s.numCertificado}</span>
            <span className="ml-2 text-xs opacity-80">
              El borrador y el PDF firmado se gestionan en la pantalla del certificado.
            </span>
          </div>
          <Link
            to={`/telecom/certificados/${s.certificadoId}`}
            className="inline-flex items-center gap-1 rounded bg-estado-ok px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <FileText className="h-4 w-4" /> Ver certificado
          </Link>
        </div>
      )}

      {/* Responsable interno por etapa (RF-03) — solo visible para internos */}
      <AsignacionPanel instanciaId={s.instanciaId} solicitudId={s.id} folio={s.folio} sector="telecom" />

      {/* Encabezado de pre-solicitud */}
      <Section title="Solicitud">
        <Field label="Folio" value={<span className="font-mono">{s.folio}</span>} />
        <Field label="Pre-solicitud" value={<span className="font-mono">{s.preSolicitud}</span>} />
        <Field label="Fecha de registro" value={formatDate(s.fechaCreacion, true)} />
        <Field label="Estatus interno" value={s.estatus} />
        {s.numReferencia && <Field label="Núm. de referencia (Finanzas)" value={<span className="font-mono">{s.numReferencia}</span>} />}
        {/* La Carta Aduana tiene su propio formato (NPM-T0037-R12); el resto usa
            el de TEL-14 (NPM-T0081-E4). Ambos, desde la aprobación en Solicitud. */}
        {s.servicioCodigo === "CARTA_ADUANA" ? (
          <Field
            label="Solicitud de Carta Aduana (PDF)"
            span={3}
            value={
              <div className="space-y-3">
                <PdfSolicitudAcciones
                  solicitudId={s.id}
                  nombreArchivo={`Solicitud-Carta-Aduana-${s.preSolicitud}.pdf`}
                  disponible={s.pdfSolicitudDisponible}
                  endpoint="pdf-carta-aduana"
                />
                <SolicitudCartaAduanaFirmada solicitudId={s.id} disponible={s.pdfSolicitudDisponible} />
              </div>
            }
          />
        ) : (
          <Field
            label="PDF de la solicitud"
            span={3}
            value={
              <PdfSolicitudAcciones
                solicitudId={s.id}
                nombreArchivo={`Solicitud-${s.preSolicitud}.pdf`}
                disponible={s.pdfSolicitudDisponible}
              />
            }
          />
        )}
      </Section>

      {/* Partes */}
      <Section title="Datos del solicitante">
        <Field
          label="Titular"
          span={2}
          value={
            <Link to={`/registros-legales/${s.titularId}`} className="text-primary-strong hover:underline">
              {s.titularRazonSocial}
            </Link>
          }
        />
        <Field label="RFC titular" value={<span className="font-mono">{s.titularRfc}</span>} />
        {s.tramitadorId && (
          <>
            <Field
              label="Tramitador externo"
              span={2}
              value={
                <Link to={`/registros-legales/${s.tramitadorId}`} className="text-primary-strong hover:underline">
                  {s.tramitadorRazonSocial}
                </Link>
              }
            />
            <Field label="RFC tramitador" value={<span className="font-mono">{s.tramitadorRfc}</span>} />
          </>
        )}
      </Section>

      {/* Servicio */}
      <Section title="Servicio solicitado">
        <Field label="Servicio" value={s.servicioNombre ?? s.servicioCodigo ?? "—"} />
        {s.otroServicio && <Field label='"Otro"' value={s.otroServicio} />}
        {isCU && (
          <>
            <Field
              label="Esquema"
              value={
                <>
                  <div>{s.esquemaNombre ?? s.esquemaCodigo ?? "—"}</div>
                  {s.esquemaDescripcion && (
                    <div className="mt-0.5 text-xs leading-snug text-fg-muted">{s.esquemaDescripcion}</div>
                  )}
                </>
              }
            />
            <Field label="Tipo de producto" value={s.tipoProductoNombre ?? s.tipoProductoCodigo ?? "—"} />
          </>
        )}
      </Section>

      {/* Producto */}
      <Section title="Datos del producto">
        <Field label="Producto" value={s.productoNombre} />
        <Field label="Modelo" value={s.productoModelo} />
        <Field label="Marca" value={s.marca} />
        {s.productoModelos && <Field label="Modelos adicionales" value={s.productoModelos} span={3} />}
        {s.productoAccesorios && <Field label="Accesorios" value={s.productoAccesorios} />}
        {s.productoFabricante && <Field label="Fabricante" value={s.productoFabricante} />}
        {s.productoFraccionArancelaria && (
          <Field label="Fracción arancelaria" value={s.productoFraccionArancelaria} />
        )}
        {s.productoCantidadLote != null && (
          <Field label="Productos por lote" value={s.productoCantidadLote.toLocaleString("es-MX")} />
        )}
        {s.productoCantidadSerie != null && (
          <Field label="Números de serie" value={s.productoCantidadSerie.toLocaleString("es-MX")} />
        )}
        {s.productoNumImeis != null && (
          <Field label="Cantidad de IMEIs" value={s.productoNumImeis.toLocaleString("es-MX")} />
        )}
        {s.paisesOrigen.length > 0 && (
          <Field
            label="País(es) de origen"
            span={3}
            value={
              <PaisesBadges paises={s.paisesOrigen} />
            }
          />
        )}
        {s.paisesProcedencia.length > 0 && (
          <Field
            label="País(es) de procedencia"
            span={3}
            value={
              <PaisesBadges paises={s.paisesProcedencia} />
            }
          />
        )}
      </Section>

      {/* Datos adicionales */}
      {(s.certificadoAnterior || s.bajoProtesta || s.numFamiliaGrupos) && (
        <Section title="Datos adicionales">
          {s.certificadoAnterior && (
            <Field label="No. de certificado anterior" value={<span className="font-mono">{s.certificadoAnterior}</span>} />
          )}
          {s.bajoProtesta != null && (
            <Field label="Bajo protesta de decir verdad" value={s.bajoProtesta ? "Sí" : "No"} />
          )}
          {s.numFamiliaGrupos && (
            <Field label="Núm. de familia(s) o grupo(s)" value={s.numFamiliaGrupos} />
          )}
        </Section>
      )}

      {/* Bodega */}
      {s.bodega && (
        <Section title="Datos de la bodega o punto de venta">
          {s.bodega.mismoDomicilioFiscal && (
            <Field
              label="Domicilio"
              span={3}
              value={<span className="text-estado-ok">Igual al domicilio fiscal del titular</span>}
            />
          )}
          <Field label="Calle" value={s.bodega.calle} />
          <Field label="Núm. exterior" value={s.bodega.numeroExterior} />
          <Field label="Núm. interior" value={s.bodega.numeroInterior} />
          <Field label="Colonia" value={s.bodega.colonia} />
          <Field label="Municipio" value={s.bodega.municipio} />
          <Field label="Entidad federativa" value={s.bodega.entidadFederativa} />
          <Field label="CP" value={<span className="font-mono">{s.bodega.codigoPostal}</span>} />
          <Field label="País" value={<span className="font-mono">{s.bodega.pais}</span>} />
          <Field label="Responsable" value={s.bodega.nombreResponsable} span={2} />
          <Field label="Teléfono" value={s.bodega.telefonoResponsable} />
        </Section>
      )}

      {/* NOMs/DT aplicables */}
      {s.normas.length > 0 && (
        <Section title={`NOM / DT aplicables (${s.normas.length})`}>
          <Field
            label="Normas"
            span={3}
            value={
              <ul className="space-y-3">
                {s.normas.map((n) => (
                  <li key={n.id} className="rounded border bg-surface-2 p-3">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono text-sm font-semibold text-fg">{n.normaCodigo}</span>
                      <span className="text-xs text-fg-muted">{n.normaTitulo}</span>
                    </div>
                    {n.laboratorioRazonSocial ? (
                      <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                        <Row label="Laboratorio" value={n.laboratorioRazonSocial} />
                        {n.numReporte && <Row label="No. de reporte" value={<span className="font-mono">{n.numReporte}</span>} />}
                        {n.modelosEvaluados && <Row label="Modelos evaluados" value={n.modelosEvaluados} />}
                        {n.fechaEmision && <Row label="Fecha de emisión" value={n.fechaEmision} />}
                        {n.diasVigencia != null && <Row label="Vigencia (días)" value={n.diasVigencia} />}
                        {n.signatarios.length > 0 && (
                          <Row
                            label="Signatarios"
                            value={n.signatarios.map((sg) => sg.nombreCompleto).join(" · ")}
                          />
                        )}
                      </dl>
                    ) : (
                      <div className="mt-2 text-xs text-fg-muted">
                        <span className="font-medium">IMEIs declarados:</span>{" "}
                        {n.numImeis != null ? n.numImeis.toLocaleString("es-MX") : "—"}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            }
          />
        </Section>
      )}

      {/* Comentario inicial */}
      {s.comentario && (
        <Section title="Comentario del solicitante">
          <Field label="Texto" span={3} value={<p className="whitespace-pre-line">{s.comentario}</p>} />
        </Section>
      )}

      {/* Copia Fiel: borrador del certificado base + certificado firmado */}
      {s.servicioCodigo === "COPIA_FIEL" && <CopiaFielPanel solicitudId={s.id} />}

      {/* Carta Aduana (#15): detalle del formato NPM-T0037-R12 */}
      {s.servicioCodigo === "CARTA_ADUANA" && <CartaAduanaPanel solicitudId={s.id} />}

      {/* Corrección de Carta Aduana (#30): DICE/DEBE DECIR + vínculo a la original */}
      {s.servicioCodigo === "CORRECCION_CARTA_ADUANA" && (
        <CorreccionCartaAduanaPanel solicitudId={s.id} />
      )}

      {/* Carta de Opinión Técnica (#17): detalle del formato NPM-T0036-E6.
          Los documentos de la emisión salen del panel genérico (tipos_documento_final). */}
      {s.servicioCodigo === "OPINION_TECNICA" && <OpinionTecnicaPanel solicitudId={s.id} />}

      {/* Documentos de la emisión, genérico por configuración del servicio
          (tipos_documento_final). Carta Aduana y Copia Fiel conservan su panel
          propio; el primer tipo configurado se distingue como «Principal». */}
      {s.servicioCodigo !== "CARTA_ADUANA" &&
        s.servicioCodigo !== "COPIA_FIEL" &&
        s.cerrada &&
        s.estadoCodigo === "ACEPTADA" &&
        (s.tiposDocumentoFinal?.length ?? 0) > 0 && (
          <Section title="Documentos de la emisión">
            <Field
              label="Emisión"
              span={3}
              value={
                <EmisionDocumentosPanel
                  solicitudId={s.id}
                  tipos={s.tiposDocumentoFinal}
                  tipoPrincipal={s.tiposDocumentoFinal[0]}
                  textoBoton="Subir documentos de la emisión"
                />
              }
            />
          </Section>
        )}

      {/* Documentos de la solicitud: mismo bloque que ya tiene Seguridad. */}
      <DocumentosDeLaSolicitud solicitudId={s.id} />

      {/* Expediente técnico (Fase 2) — sigue siendo útil para Analistas */}
      <Section title="Expediente técnico">
        <Field
          label="Acceso"
          span={3}
          value={
            <Link
              to={`/sector/telecom/expedientes/por-solicitud/${s.id}`}
              className="text-primary-strong hover:underline"
            >
              Ver expediente y documentos vinculados →
            </Link>
          }
        />
      </Section>

      {/* Secciones de Fase 2 — solo aplican al flujo Telecom v1 (OBSOLETO).
          Para el flujo simple v2 estos pasos no existen, así que las ocultamos. */}
      {esFlujoTecnicoLegacy(s.estadoCodigo) && (
        <>
          <LaboratorioAsignadoSection solicitudId={s.id} estadoCodigo={s.estadoCodigo} />
          <MuestrasSection solicitudId={s.id} />
          <ReportesPruebaSection
            solicitudId={s.id}
            solicitud={{
              marca: s.marca,
              productoModelo: s.productoModelo,
              normaCodigo: s.normas[0]?.normaCodigo ?? "",
              estadoCodigo: s.estadoCodigo,
            }}
          />
        </>
      )}

      {/* Histórico cronológico */}
      <Section title={`Histórico de cambios de estado (${s.historico.length})`}>
        <Field
          label="Línea de tiempo"
          span={3}
          value={
            s.historico.length === 0 ? (
              <span className="text-sm text-fg-subtle">Sin transiciones registradas.</span>
            ) : (
              <ol className="space-y-2">
                {s.historico.map((h, i) => (
                  <li key={i} className="flex flex-wrap items-start gap-3 rounded border bg-surface p-3 text-sm">
                    <div className="text-xs text-fg-subtle">{formatDate(h.ts, true)}</div>
                    <div className="flex-1">
                      <div className="font-medium text-fg">
                        {h.transicionNombre ?? h.transicionCodigo ?? "Transición"}
                      </div>
                      <div className="text-xs text-fg-muted">
                        {h.estadoOrigenCodigo ? <><span className="font-mono">{h.estadoOrigenCodigo}</span> → </> : null}
                        <span className="font-mono text-fg">{h.estadoDestinoCodigo}</span>
                      </div>
                      {h.motivo && (
                        <div className="mt-1 whitespace-pre-line rounded bg-surface-2 p-2 text-xs text-fg-muted">
                          {h.motivo}
                        </div>
                      )}
                    </div>
                    {h.actorCorreo && (
                      <div className="shrink-0 text-xs text-fg-subtle">{h.actorCorreo}</div>
                    )}
                  </li>
                ))}
              </ol>
            )
          }
        />
      </Section>

      {/* IDs internos: solo para ADMIN_SICEN (soporte); confunden al usuario final */}
      {esAdmin && (
        <Section title="Metadatos">
          <Field label="ID" value={<span className="font-mono">{s.id}</span>} span={2} />
          <Field label="Instancia ID" value={<span className="font-mono">{s.instanciaId}</span>} />
        </Section>
      )}
    </DetailLayout>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="font-semibold text-fg-muted">{label}:</dt>
      <dd className="text-fg">{value}</dd>
    </div>
  );
}

/** Documentos del trámite completo (solicitud + expediente). */
const DOCS_TRAMITE_QK = (solicitudId: string) => ["documentos-tramite", solicitudId];

/**
 * Todos los documentos del trámite en un solo lugar, con descarga individual,
 * descarga conjunta en ZIP y el hilo de comentarios de cada uno. Es el mismo
 * bloque que ya tenía Seguridad; lo único propio de Telecom es el botón «Ver»,
 * que abre la vista previa con la que aquí se trabaja desde siempre.
 *
 * Se pide por trámite y no por contexto porque los documentos de Telecom cuelgan
 * del expediente, no de la solicitud: preguntar por `SOLICITUD_TELECOM` devolvía
 * una lista vacía aunque el trámite tuviera catorce documentos, y por eso desde
 * esta pantalla no se veía ninguno.
 *
 * De consulta a propósito: subir, versionar y retirar se siguen haciendo en el
 * expediente, donde viven la vinculación por tipo y la completitud que dispara
 * la etapa. Ver `ExpedienteTelecomPage`.
 */
function DocumentosDeLaSolicitud({ solicitudId }: { solicitudId: string }) {
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const qDocs = useQuery<DocResumen[]>({
    queryKey: DOCS_TRAMITE_QK(solicitudId),
    queryFn: async () =>
      (await api.get<DocResumen[]>(`/api/documentos/tramite/${solicitudId}`)).data,
  });
  const docs = qDocs.data ?? [];

  return (
    <Section title={`Documentos de la solicitud${docs.length > 0 ? ` (${docs.length})` : ""}`}>
      {/* A todo el ancho: el título de la sección ya dice qué es, un rótulo
          encima de la lista solo repetiría. */}
      <div className="sm:col-span-2 lg:col-span-3">
        {qDocs.isLoading ? (
          <span className="text-sm text-fg-subtle">Cargando documentos…</span>
        ) : docs.length === 0 ? (
          <div className="rounded border border-dashed border-edge-strong p-4 text-center text-sm text-fg-subtle">
            Aún no hay documentos adjuntos a esta solicitud.
          </div>
        ) : (
          <DocumentosSeleccionables
            docs={docs}
            tramiteId={solicitudId}
            permitirEscritura={false}
            motivoDescarga="Descarga desde detalle de solicitud Telecom"
            renderMeta={(d) => (
              <>
                <span>· Cargado {formatDate(d.fechaCarga, true)}</span>
                {(d as DocResumen).antivirusEstado === "LIMPIO" ? (
                  <span className="inline-flex items-center gap-1 text-estado-ok"><ShieldCheck className="h-3 w-3" /> Limpio</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-estado-warn"><ShieldAlert className="h-3 w-3" /> {(d as DocResumen).antivirusEstado}</span>
                )}
              </>
            )}
            renderAccionExtra={(d) => (
              <button
                type="button"
                onClick={() => setPreviewDocId(d.documentoId)}
                className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
              >
                <Eye className="h-3 w-3" /> Ver
              </button>
            )}
          />
        )}
      </div>

      {previewDocId && (
        <DocumentoPreviewModal documentoId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </Section>
  );
}

// ============================================================================
// Copia Fiel: cierre del flujo (borrador del base + certificado firmado)
// ============================================================================

interface DocResumen {
  documentoId: string;
  /** El backend ya lo devolvía; hacía falta declararlo para la descarga múltiple. */
  versionVigenteId: string;
  tipoCodigo: string;
  tipoNombre: string;
  nombreArchivo: string;
  sizeBytes: number;
  antivirusEstado: string;
  fechaCarga: string;
}

const TIPO_COPIA_FIEL_FIRMADO = "COPIA_FIEL_CERT_FIRMADO";
const ROLES_INTERNOS = new Set([
  "ADMIN_SICEN", "RESPONSABLE_TELECOM", "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO", "AUDITOR_INTERNO", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD",
]);
// Carta Aduana: facultad distinta de la del certificado firmado, se deja como estaba.
const ROLES_SUBIR_CARTA_ADUANA = new Set(["ADMIN_SICEN", "RESPONSABLE_TELECOM", "COORDINADOR_TECNICO"]);


interface CartaAduanaDetalle {
  normas: string[];
  series: string[];
  sinSerie: boolean;
  justificacionSerie: string | null;
  accesorios: string;
  paisesOrigen: string[];
  paisesProcedencia: string[];
  /** Mismo snapshot resuelto contra el catálogo: clave ISO + nombre. */
  paisesOrigenRef: PaisSnapshotRef[];
  paisesProcedenciaRef: PaisSnapshotRef[];
  fracciones: string[];
  numMuestras: number;
  aduanas: { numero: string; nombre: string }[];
  laboratorio: string;
  representanteNombre: string;
  observaciones: string | null;
  /** Correcciones (#30) que referencian esta carta (RN-18). */
  correcciones: { id: string; folio: string }[];
}

const TIPO_CA_SOLICITUD_FIRMADA = "CARTA_ADUANA_SOLICITUD_FIRMADA";

/**
 * Solicitud de Carta Aduana firmada: NPMX imprime el PDF generado (NPM-T0037-R12),
 * lo firma a mano y sube aquí el escaneo. Mismo patrón que el certificado
 * firmado de Copia Fiel, incluido el permiso; la carga se habilita junto con
 * el PDF (aprobación en la etapa Solicitud).
 */
function SolicitudCartaAduanaFirmada({ solicitudId, disponible }: { solicitudId: string; disponible: boolean }) {
  const qc = useQueryClient();
  // Misma facultad que el certificado firmado de Copia Fiel: quién puede vive
  // en kernel.rol_permiso, no aquí (decisión de NPMX del 16-10).
  const puedeSubir = useTienePermiso(PERMISO_SUBIR_CERTIFICADO_FIRMADO);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const qDocs = useQuery<DocResumen[]>({
    queryKey: ["carta-aduana-solicitud-firmada", solicitudId],
    queryFn: async () =>
      (await api.get<DocResumen[]>(
        `/api/documentos/contexto?contextoTipo=SOLICITUD_TELECOM&contextoId=${solicitudId}`,
      )).data,
  });
  const firmadas = (qDocs.data ?? []).filter((d) => d.tipoCodigo === TIPO_CA_SOLICITUD_FIRMADA);

  if (!disponible && firmadas.length === 0) return null;

  return (
    <div className="space-y-2">
      {disponible && puedeSubir && (
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <Upload className="h-4 w-4" /> Subir solicitud firmada
        </button>
      )}
      <div className="rounded border">
        <div className="border-b bg-surface-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Solicitud firmada ({firmadas.length})
        </div>
        {qDocs.isLoading ? (
          <div className="p-3 text-sm text-fg-subtle">Cargando…</div>
        ) : firmadas.length === 0 ? (
          <div className="p-3 text-sm text-fg-subtle">
            Aún no se ha subido la solicitud firmada. Imprime el PDF, fírmalo y súbelo escaneado.
          </div>
        ) : (
          <ul className="divide-y">
            {firmadas.map((doc) => (
              <li key={doc.documentoId} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-fg-faint" />
                  <div className="min-w-0">
                    <span className="block truncate font-mono text-xs text-fg-muted" title={doc.nombreArchivo}>
                      {doc.nombreArchivo}
                    </span>
                    <div className="flex items-center gap-2 text-[11px] text-fg-subtle">
                      <span>Cargado {formatDate(doc.fechaCarga, true)}</span>
                      {doc.antivirusEstado === "LIMPIO" ? (
                        <span className="inline-flex items-center gap-1 text-estado-ok"><ShieldCheck className="h-3 w-3" /> Limpio</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-estado-warn"><ShieldAlert className="h-3 w-3" /> {doc.antivirusEstado}</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewDocId(doc.documentoId)}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                >
                  <Eye className="h-3 w-3" /> Ver / descargar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {uploadOpen && (
        <UploadDocumentoModal
          open={uploadOpen}
          title="Subir solicitud de Carta Aduana firmada"
          contextoTipo="SOLICITUD_TELECOM"
          contextoId={solicitudId}
          tipoCodigoFijo={TIPO_CA_SOLICITUD_FIRMADA}
          onClose={() => setUploadOpen(false)}
          onUploaded={async () => {
            await qc.invalidateQueries({ queryKey: ["carta-aduana-solicitud-firmada", solicitudId] });
            await qc.invalidateQueries({ queryKey: DOCS_TRAMITE_QK(solicitudId) });
          }}
        />
      )}

      {previewDocId && (
        <DocumentoPreviewModal documentoId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </div>
  );
}

const TIPO_CA_EMITIDA = "CARTA_ADUANA_EMITIDA";
const TIPO_CA_ADICIONAL = "CARTA_ADUANA_EMISION_ADICIONAL";

/** Detalle específico de la Carta Aduana (#15, formato NPM-T0037-R12). */
function CartaAduanaPanel({ solicitudId }: { solicitudId: string }) {
  const qc = useQueryClient();
  const roles = useRolesEfectivos();
  const puedeSubir = roles.some((r) => ROLES_SUBIR_CARTA_ADUANA.has(r));
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const q = useQuery<CartaAduanaDetalle>({
    queryKey: ["carta-aduana-detalle", solicitudId],
    queryFn: async () =>
      (await api.get<CartaAduanaDetalle>(
        `/api/sector/telecom/solicitudes/${solicitudId}/carta-aduana`,
      )).data,
  });

  // La carga de documentos finales solo se habilita cuando TODAS las etapas
  // concluyeron con aceptación (flujo cerrado en ACEPTADA).
  const qEstado = useQuery<{ cerrada: boolean; estado: { codigo: string } }>({
    queryKey: ["solicitud-estado", solicitudId],
    queryFn: async () =>
      (await api.get<{ cerrada: boolean; estado: { codigo: string } }>(
        `/api/sector/telecom/solicitudes/${solicitudId}/estado`,
      )).data,
  });
  const emitida = !!qEstado.data?.cerrada && qEstado.data.estado.codigo === "ACEPTADA";

  const qDocs = useQuery<DocResumen[]>({
    queryKey: ["carta-aduana-emision-docs", solicitudId],
    queryFn: async () =>
      (await api.get<DocResumen[]>(
        `/api/documentos/contexto?contextoTipo=SOLICITUD_TELECOM&contextoId=${solicitudId}`,
      )).data,
  });
  const docsEmision = (qDocs.data ?? []).filter(
    (d) => d.tipoCodigo === TIPO_CA_EMITIDA || d.tipoCodigo === TIPO_CA_ADICIONAL,
  );
  if (q.isLoading) {
    return (
      <Section title="Carta Aduana (NPM-T0037-R12)">
        <Field label="Detalle" span={3} value="Cargando…" />
      </Section>
    );
  }
  const d = q.data;
  if (!d) return null;
  return (
    <Section title="Carta Aduana (NPM-T0037-R12)">
      <Field label="Norma(s) / DT" span={3} value={d.normas.join(", ")} />
      <Field
        label="Número(s) de serie"
        span={3}
        value={
          d.sinSerie
            ? `Sin número de serie — ${d.justificacionSerie ?? ""}`
            : d.series.join(", ")
        }
      />
      <Field label="Accesorios" value={d.accesorios} />
      <Field
        label="País(es) de origen"
        span={3}
        value={(d.paisesOrigenRef ?? []).length > 0 ? <PaisesBadges paises={d.paisesOrigenRef} /> : null}
      />
      <Field
        label="País(es) de procedencia"
        span={3}
        value={(d.paisesProcedenciaRef ?? []).length > 0 ? <PaisesBadges paises={d.paisesProcedenciaRef} /> : null}
      />
      <Field label="Fracción(es) arancelaria(s)" value={d.fracciones.join(", ")} />
      <Field label="Número de muestras" value={String(d.numMuestras)} />
      <Field label="Laboratorio" value={d.laboratorio} />
      <Field
        label="Aduana(s)"
        span={3}
        value={d.aduanas.map((a) => `${a.nombre} (No. ${a.numero})`).join(" · ")}
      />
      <Field label="Representante autorizado" value={d.representanteNombre} />
      {d.observaciones && (
        <Field
          label="Observaciones"
          span={2}
          value={<p className="whitespace-pre-line">{d.observaciones}</p>}
        />
      )}
      {(d.correcciones ?? []).length > 0 && (
        <Field
          label="Corregida por"
          span={3}
          value={
            <span className="flex flex-wrap gap-2">
              {d.correcciones.map((c) => (
                <Link
                  key={c.id}
                  to={`/sector/telecom/solicitudes/${c.id}`}
                  className="text-primary-strong underline-offset-2 hover:underline"
                >
                  {c.folio}
                </Link>
              ))}
            </span>
          }
        />
      )}

      {/* Carga y consulta de la carta emitida — solo con el flujo concluido en ACEPTADA */}
      {(emitida || docsEmision.length > 0) && (
        <Field
          label="Carta aduana emitida"
          span={3}
          value={
            <div className="space-y-3">
              {emitida && puedeSubir && (
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover"
                >
                  <Upload className="h-4 w-4" /> Subir carta aduana y documentos adicionales
                </button>
              )}
              <div className="rounded border">
                <div className="border-b bg-surface-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                  Documentos de la emisión ({docsEmision.length})
                </div>
                {qDocs.isLoading ? (
                  <div className="p-3 text-sm text-fg-subtle">Cargando…</div>
                ) : docsEmision.length === 0 ? (
                  <div className="p-3 text-sm text-fg-subtle">
                    Aún no se ha subido la carta aduana emitida.
                  </div>
                ) : (
                  <ul className="divide-y">
                    {docsEmision.map((doc) => (
                      <li key={doc.documentoId} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0 text-fg-faint" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="truncate font-mono text-xs text-fg-muted" title={doc.nombreArchivo}>
                                {doc.nombreArchivo}
                              </span>
                              {doc.tipoCodigo === TIPO_CA_EMITIDA ? (
                                <StatusBadge tone="ok">Principal</StatusBadge>
                              ) : (
                                <StatusBadge tone="neutral">Adicional</StatusBadge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-fg-subtle">
                              <span>Cargado {formatDate(doc.fechaCarga, true)}</span>
                              {doc.antivirusEstado === "LIMPIO" ? (
                                <span className="inline-flex items-center gap-1 text-estado-ok"><ShieldCheck className="h-3 w-3" /> Limpio</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-estado-warn"><ShieldAlert className="h-3 w-3" /> {doc.antivirusEstado}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPreviewDocId(doc.documentoId)}
                          className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                        >
                          <Eye className="h-3 w-3" /> Ver / descargar
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          }
        />
      )}

      {uploadOpen && (
        <UploadDocumentoModal
          open={uploadOpen}
          title="Subir carta aduana y documentos adicionales"
          contextoTipo="SOLICITUD_TELECOM"
          contextoId={solicitudId}
          tag="TELECOM_CARTA_ADUANA_EMISION"
          onClose={() => setUploadOpen(false)}
          onUploaded={async () => {
            await qc.invalidateQueries({ queryKey: ["carta-aduana-emision-docs", solicitudId] });
          }}
        />
      )}

      {previewDocId && (
        <DocumentoPreviewModal documentoId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </Section>
  );
}

interface CorreccionCaDetalle {
  motivo: string;
  cartaOriginalId: string;
  cartaOriginalFolio: string;
  cambios: { campo: string; dice: string; debeDecir: string }[];
}

const CAMPO_CA_LABEL: Record<string, string> = {
  PRODUCTO_NOMBRE: "Nombre del producto",
  MARCA: "Marca",
  MODELO: "Modelo",
  SERIES: "Número(s) de serie",
  ACCESORIOS: "Accesorios",
  PAISES_ORIGEN: "País(es) de origen",
  PAISES_PROCEDENCIA: "País(es) de procedencia",
  FRACCIONES: "Fracción(es) arancelaria(s)",
  NUM_MUESTRAS: "Número de muestras",
  ADUANAS: "Aduana(s)",
  LABORATORIO: "Laboratorio",
  NORMAS: "Norma(s) / DT",
};

/** Detalle específico de la Corrección de Carta Aduana (#30): DICE/DEBE DECIR. */
function CorreccionCartaAduanaPanel({ solicitudId }: { solicitudId: string }) {
  const q = useQuery<CorreccionCaDetalle>({
    queryKey: ["correccion-ca-detalle", solicitudId],
    queryFn: async () =>
      (await api.get<CorreccionCaDetalle>(
        `/api/sector/telecom/solicitudes/${solicitudId}/correccion-carta-aduana`,
      )).data,
  });
  if (q.isLoading) {
    return (
      <Section title="Corrección de Carta Aduana (#30)">
        <Field label="Detalle" span={3} value="Cargando…" />
      </Section>
    );
  }
  const d = q.data;
  if (!d) return null;
  return (
    <Section title="Corrección de Carta Aduana (#30)">
      <Field
        label="Carta Aduana original"
        span={3}
        value={
          <Link
            to={`/sector/telecom/solicitudes/${d.cartaOriginalId}`}
            className="text-primary-strong underline-offset-2 hover:underline"
          >
            {d.cartaOriginalFolio}
          </Link>
        }
      />
      <Field
        label="Cambios solicitados"
        span={3}
        value={
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-fg-subtle">
                  <th className="py-1.5 pr-3">Campo</th>
                  <th className="py-1.5 pr-3">DICE (carta actual)</th>
                  <th className="py-1.5">DEBE DECIR</th>
                </tr>
              </thead>
              <tbody>
                {d.cambios.map((c) => (
                  <tr key={c.campo} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-3 font-medium">
                      {CAMPO_CA_LABEL[c.campo] ?? c.campo}
                    </td>
                    <td className="py-1.5 pr-3 text-fg-muted">{c.dice || "—"}</td>
                    <td className="py-1.5 font-medium">{c.debeDecir}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      />
      <Field
        label="Motivo"
        span={3}
        value={<p className="whitespace-pre-line">{d.motivo}</p>}
      />
    </Section>
  );
}

interface OpinionTecnicaDetalle {
  normas: string[];
  modelos: { marca: string; modelo: string; fabricante: string }[];
  accesorios: string;
  paisesOrigen: string[];
  paisesProcedencia: string[];
  /** Mismo snapshot resuelto contra el catálogo: clave ISO + nombre. */
  paisesOrigenRef: PaisSnapshotRef[];
  paisesProcedenciaRef: PaisSnapshotRef[];
  fracciones: string[];
  equipoNoNuevo: boolean;
  costo: number;
  representanteNombre: string;
  observaciones: string | null;
}

/** Detalle específico de la Carta de Opinión Técnica (#17, formato NPM-T0036-E6). */
function OpinionTecnicaPanel({ solicitudId }: { solicitudId: string }) {
  const q = useQuery<OpinionTecnicaDetalle>({
    queryKey: ["opinion-tecnica-detalle", solicitudId],
    queryFn: async () =>
      (await api.get<OpinionTecnicaDetalle>(
        `/api/sector/telecom/solicitudes/${solicitudId}/opinion-tecnica`,
      )).data,
  });
  if (q.isLoading) {
    return (
      <Section title="Carta de Opinión Técnica (NPM-T0036-E6)">
        <Field label="Detalle" span={3} value="Cargando…" />
      </Section>
    );
  }
  const d = q.data;
  if (!d) return null;
  return (
    <Section title="Carta de Opinión Técnica (NPM-T0036-E6)">
      <Field label="Norma(s) / DT" span={3} value={d.normas.join(", ")} />
      <Field
        label={`Modelos (${d.modelos.length})`}
        span={3}
        value={
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-fg-subtle">
                  <th className="py-1.5 pr-3">#</th>
                  <th className="py-1.5 pr-3">Marca</th>
                  <th className="py-1.5 pr-3">Modelo</th>
                  <th className="py-1.5">Fabricante</th>
                </tr>
              </thead>
              <tbody>
                {d.modelos.map((m, i) => (
                  <tr key={`${m.marca}-${m.modelo}`} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-3 text-fg-subtle">{i + 1}</td>
                    <td className="py-1.5 pr-3 font-medium">{m.marca}</td>
                    <td className="py-1.5 pr-3">{m.modelo}</td>
                    <td className="py-1.5">{m.fabricante}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      />
      <Field label="Accesorios" value={d.accesorios} />
      <Field
        label="País(es) de origen"
        span={3}
        value={(d.paisesOrigenRef ?? []).length > 0 ? <PaisesBadges paises={d.paisesOrigenRef} /> : null}
      />
      <Field
        label="País(es) de procedencia"
        span={3}
        value={(d.paisesProcedenciaRef ?? []).length > 0 ? <PaisesBadges paises={d.paisesProcedenciaRef} /> : null}
      />
      <Field label="Fracción(es) arancelaria(s)" value={d.fracciones.join(", ")} />
      <Field label="Equipo no nuevo" value={d.equipoNoNuevo ? "Sí (reacondicionado)" : "No"} />
      <Field label="Representante autorizado" value={d.representanteNombre} />
      {d.observaciones && (
        <Field
          label="Observaciones"
          span={3}
          value={<p className="whitespace-pre-line">{d.observaciones}</p>}
        />
      )}
    </Section>
  );
}

function CopiaFielPanel({ solicitudId }: { solicitudId: string }) {
  const qc = useQueryClient();
  const roles = useRolesEfectivos();
  const esInterno = roles.some((r) => ROLES_INTERNOS.has(r));
  // Capacidad, no rol: la lista de quién puede vive en kernel.rol_permiso.
  const puedeSubir = useTienePermiso(PERMISO_SUBIR_CERTIFICADO_FIRMADO);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [borradorLoading, setBorradorLoading] = useState(false);

  const qDocs = useQuery<DocResumen[]>({
    queryKey: ["copia-fiel-firmados", solicitudId],
    queryFn: async () =>
      (await api.get<DocResumen[]>(
        `/api/documentos/contexto?contextoTipo=SOLICITUD_TELECOM&contextoId=${solicitudId}`,
      )).data,
  });
  const firmados = (qDocs.data ?? []).filter((d) => d.tipoCodigo === TIPO_COPIA_FIEL_FIRMADO);

  const verBorrador = async () => {
    setBorradorLoading(true);
    try {
      const resp = await api.get(
        `/api/sector/telecom/solicitudes/${solicitudId}/copia-fiel/borrador`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      /* el botón solo se muestra a internos; errores quedan en consola del navegador */
    } finally {
      setBorradorLoading(false);
    }
  };

  return (
    <Section title="Copia Fiel — Certificado">
      <Field
        label="Acciones"
        span={3}
        value={
          <div className="space-y-3">
            <p className="text-sm text-fg-muted">
              La copia fiel reproduce el certificado de origen (mismo número). Revisa el borrador y
              sube el certificado firmado para que el solicitante lo descargue desde el portal.
            </p>

            {esInterno && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={verBorrador}
                  disabled={borradorLoading}
                  className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint disabled:opacity-50"
                >
                  {borradorLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Generar borrador
                </button>
                {puedeSubir && (
                  <button
                    type="button"
                    onClick={() => setUploadOpen(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover"
                  >
                    <Upload className="h-4 w-4" /> Subir certificado firmado
                  </button>
                )}
              </div>
            )}

            <div className="rounded border">
              <div className="border-b bg-surface-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                Certificado firmado ({firmados.length})
              </div>
              {qDocs.isLoading ? (
                <div className="p-3 text-sm text-fg-subtle">Cargando…</div>
              ) : firmados.length === 0 ? (
                <div className="p-3 text-sm text-fg-subtle">
                  Aún no se ha subido el certificado firmado.
                </div>
              ) : (
                <div className="px-3 pb-2">
                  <DocumentosSeleccionables
                    docs={firmados}
                    contextoTipo="SOLICITUD_TELECOM"
                    contextoId={solicitudId}
                    motivoDescarga="Descarga desde detalle de solicitud Telecom"
                    renderMeta={(d) => (
                      <>
                        <span>· Cargado {formatDate(d.fechaCarga, true)}</span>
                        {d.antivirusEstado === "LIMPIO" ? (
                          <span className="inline-flex items-center gap-1 text-estado-ok"><ShieldCheck className="h-3 w-3" /> Limpio</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-estado-warn"><ShieldAlert className="h-3 w-3" /> {d.antivirusEstado}</span>
                        )}
                      </>
                    )}
                    renderAccion={(d) => (
                      <button
                        type="button"
                        onClick={() => setPreviewDocId(d.documentoId)}
                        className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                      >
                        <Eye className="h-3 w-3" /> Ver / descargar
                      </button>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        }
      />

      {uploadOpen && (
        <UploadDocumentoModal
          open={uploadOpen}
          title="Subir certificado firmado (Copia Fiel)"
          contextoTipo="SOLICITUD_TELECOM"
          contextoId={solicitudId}
          tipoCodigoFijo={TIPO_COPIA_FIEL_FIRMADO}
          onClose={() => setUploadOpen(false)}
          onUploaded={async () => {
            await qc.invalidateQueries({ queryKey: ["copia-fiel-firmados", solicitudId] });
          }}
        />
      )}

      {previewDocId && (
        <DocumentoPreviewModal documentoId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </Section>
  );
}
